import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { ValidationScoreboard, ValidationMetrics } from "@/components/academician/validation-scoreboard"
import { StartupProfileTab, StartupProfile } from "@/components/academician/startup-profile-tab"
import { PainPointsTab, PainPoint } from "@/components/academician/pain-points-tab"
import { CustomerSegmentsTab, CustomerSegment } from "@/components/academician/customer-segments-tab"
import { ContactsTab, Contact } from "@/components/academician/contacts-tab"
import { ExistingAlternativesTab, ExistingAlternative } from "@/components/academician/existing-alternatives-tab"
import { InterviewsTab, Interview } from "@/components/academician/interviews-tab"
import { FoundingFoundersTab, FoundingFounder } from "@/components/academician/founding-founders-tab"
import { TeamSubmissions, BrainstormingSubmissionItem, MeetupUploadItem } from "@/components/academician/team-submissions"

interface ApiPaths {
  startup: string
  segments: string
  painPoints: string
  existingAlternatives: string
  contacts: string
  interviews: string
  startupMembers: string
  bulkImportContacts?: string
  importContactsTemplate?: string
}

export interface MyStartupPageProps {
  startupName?: string
  metrics?: ValidationMetrics
  profile?: StartupProfile
  painPoints?: PainPoint[]
  existingAlternatives?: ExistingAlternative[]
  segments?: CustomerSegment[]
  contacts?: Contact[]
  interviews?: Interview[]
  foundingFounders?: FoundingFounder[]
  teamBrainstormingSubmissions?: BrainstormingSubmissionItem[]
  teamMeetupUploads?: MeetupUploadItem[]
  isOwner?: boolean
  csrfToken?: string
  apiPaths?: ApiPaths
  className?: string
}


async function apiCall(url: string, method: string, csrfToken: string, body?: Record<string, unknown>) {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      "Accept": "application/json"
    },
    body: body ? JSON.stringify(body) : undefined
  })
  if (!response.ok) {
    const data = await response.json().catch(() => null)
    const msg = data?.errors?.join(", ") || "Something went wrong"
    alert(msg)
    return null
  }
  return response
}

// Map React status to Rails status (shared by pain points and existing alternatives)
function statusToRails(status: string): string {
  switch (status) {
    case "testing": return "validating"
    case "validated": return "validated"
    case "invalidated": return "invalidated"
    default: return "hypothesis"
  }
}

// Map React pain point status to Rails status
function painPointStatusToRails(status: string): string {
  switch (status) {
    case "testing": return "validating"
    case "validated": return "validated"
    case "invalidated": return "invalidated"
    default: return "hypothesis"
  }
}

// Map React segment status to Rails status
function segmentStatusToRails(status: string): string {
  return status === "inactive" ? "pivoted" : "active"
}

// Map React interview type to Rails enum
function interviewTypeToRails(type: string): string {
  switch (type) {
    case "video-call": return "video_call"
    case "phone": return "phone_call"
    case "in-person": return "in_person"
    default: return "video_call"
  }
}

export function MyStartupPage({
  startupName: initialStartupName,
  profile,
  painPoints: initialPainPoints = [],
  existingAlternatives: initialExistingAlternatives = [],
  segments: initialSegments = [],
  contacts: initialContacts = [],
  interviews: initialInterviews = [],
  foundingFounders: initialFoundingFounders = [],
  teamBrainstormingSubmissions = [],
  teamMeetupUploads = [],
  isOwner = false,
  csrfToken = "",
  apiPaths,
  className
}: MyStartupPageProps) {
  const [startupName, setStartupName] = useState(initialStartupName || "")

  const [painPoints, setPainPoints] = useState(initialPainPoints)
  const [existingAlternatives, setExistingAlternatives] = useState(initialExistingAlternatives)
  const [segments, setSegments] = useState(initialSegments)
  const [contacts, setContacts] = useState(initialContacts)
  const [interviews, setInterviews] = useState(initialInterviews)
  const [foundingFounders, setFoundingFounders] = useState(initialFoundingFounders)

  const handleSaveProfile = async (profileData: StartupProfile) => {
    if (!apiPaths) return
    const resp = await apiCall(apiPaths.startup, "PATCH", csrfToken, {
      startup: {
        name: profileData.name,
        one_liner: profileData.oneLiner
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setStartupName(profileData.name)
      setTempName(profileData.name)
    }
  }

  // --- Segment CRUD ---
  const handleAddSegment = async (segment: Omit<CustomerSegment, "id">) => {
    if (!apiPaths) {
      setSegments([...segments, { ...segment, id: Date.now().toString() }])
      return
    }
    const resp = await apiCall(apiPaths.segments, "POST", csrfToken, {
      customer_segment: {
        name: segment.name,
        description: segment.description,
        status: segmentStatusToRails(segment.status)
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setSegments([...segments, data.segment])
    }
  }

  const handleEditSegment = async (id: string, segment: Omit<CustomerSegment, "id">) => {
    if (!apiPaths) {
      setSegments(segments.map((s) => (s.id === id ? { ...segment, id } : s)))
      return
    }
    const resp = await apiCall(`${apiPaths.segments}/${id}`, "PATCH", csrfToken, {
      customer_segment: {
        name: segment.name,
        description: segment.description,
        status: segmentStatusToRails(segment.status)
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setSegments(segments.map((s) => (s.id === id ? data.segment : s)))
    }
  }

  const handleDeleteSegment = async (id: string) => {
    if (!apiPaths) {
      setSegments(segments.filter((s) => s.id !== id))
      return
    }
    const resp = await apiCall(`${apiPaths.segments}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setSegments(segments.filter((s) => s.id !== id))
    }
  }

  // --- Pain Point CRUD ---
  const handleAddPainPoint = async (painPoint: Omit<PainPoint, "id">) => {
    if (!apiPaths) {
      setPainPoints([...painPoints, { ...painPoint, id: Date.now().toString() }])
      return
    }
    const matchingSegment = segments.find(s => s.name === painPoint.segment)
    const resp = await apiCall(apiPaths.painPoints, "POST", csrfToken, {
      pain_point: {
        description: painPoint.painPoint,
        status: painPointStatusToRails(painPoint.status),
        customer_segment_id: matchingSegment?.id || null,
        severity: 3
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setPainPoints([...painPoints, data.painPoint])
    }
  }

  const handleEditPainPoint = async (id: string, painPoint: Omit<PainPoint, "id">) => {
    if (!apiPaths) {
      setPainPoints(painPoints.map((p) => (p.id === id ? { ...painPoint, id } : p)))
      return
    }
    const matchingSegment = segments.find(s => s.name === painPoint.segment)
    const resp = await apiCall(`${apiPaths.painPoints}/${id}`, "PATCH", csrfToken, {
      pain_point: {
        description: painPoint.painPoint,
        status: painPointStatusToRails(painPoint.status),
        customer_segment_id: matchingSegment?.id || null
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setPainPoints(painPoints.map((p) => (p.id === id ? data.painPoint : p)))
    }
  }

  const handleDeletePainPoint = async (id: string) => {
    if (!apiPaths) {
      setPainPoints(painPoints.filter((p) => p.id !== id))
      return
    }
    const resp = await apiCall(`${apiPaths.painPoints}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setPainPoints(painPoints.filter((p) => p.id !== id))
    }
  }

  // --- Existing Alternative CRUD ---
  const handleAddAlternative = async (alt: Omit<ExistingAlternative, "id">) => {
    if (!apiPaths) {
      setExistingAlternatives([...existingAlternatives, { ...alt, id: Date.now().toString() }])
      return
    }
    const matchingSegment = segments.find(s => s.name === alt.segment)
    const resp = await apiCall(apiPaths.existingAlternatives, "POST", csrfToken, {
      existing_alternative: {
        description: alt.description,
        status: statusToRails(alt.status),
        customer_segment_id: matchingSegment?.id || null
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setExistingAlternatives([...existingAlternatives, data.existingAlternative])
    }
  }

  const handleEditAlternative = async (id: string, alt: Omit<ExistingAlternative, "id">) => {
    if (!apiPaths) {
      setExistingAlternatives(existingAlternatives.map((a) => (a.id === id ? { ...alt, id } : a)))
      return
    }
    const matchingSegment = segments.find(s => s.name === alt.segment)
    const resp = await apiCall(`${apiPaths.existingAlternatives}/${id}`, "PATCH", csrfToken, {
      existing_alternative: {
        description: alt.description,
        status: statusToRails(alt.status),
        customer_segment_id: matchingSegment?.id || null
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setExistingAlternatives(existingAlternatives.map((a) => (a.id === id ? data.existingAlternative : a)))
    }
  }

  const handleDeleteAlternative = async (id: string) => {
    if (!apiPaths) {
      setExistingAlternatives(existingAlternatives.filter((a) => a.id !== id))
      return
    }
    const resp = await apiCall(`${apiPaths.existingAlternatives}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setExistingAlternatives(existingAlternatives.filter((a) => a.id !== id))
    }
  }

  // --- Contact CRUD ---
  const handleAddContact = async (contact: Omit<Contact, "id">) => {
    if (!apiPaths) {
      setContacts([...contacts, { ...contact, id: Date.now().toString() }])
      return
    }
    const matchingSegment = segments.find(s => s.name === contact.segment)
    const resp = await apiCall(apiPaths.contacts, "POST", csrfToken, {
      contact: {
        name: contact.name,
        source: contact.source,
        status: contact.status || "to_contact",
        customer_segment_id: matchingSegment?.id || null
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setContacts([...contacts, data.contact])
    }
  }

  const handleEditContact = async (id: string, contact: Omit<Contact, "id">) => {
    if (!apiPaths) {
      setContacts(contacts.map((c) => (c.id === id ? { ...contact, id } : c)))
      return
    }
    const matchingSegment = segments.find(s => s.name === contact.segment)
    const resp = await apiCall(`${apiPaths.contacts}/${id}`, "PATCH", csrfToken, {
      contact: {
        name: contact.name,
        source: contact.source,
        status: contact.status,
        customer_segment_id: matchingSegment?.id || null
      }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setContacts(contacts.map((c) => (c.id === id ? data.contact : c)))
    }
  }

  const handleDeleteContact = async (id: string) => {
    if (!apiPaths) {
      setContacts(contacts.filter((c) => c.id !== id))
      return
    }
    const resp = await apiCall(`${apiPaths.contacts}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setContacts(contacts.filter((c) => c.id !== id))
    }
  }

  // --- Founding Founder actions ---
  const handleInviteFounder = async (data: { email: string; roleTitle: string }) => {
    if (!apiPaths) return
    const resp = await apiCall(apiPaths.startupMembers, "POST", csrfToken, {
      startup_member: { email: data.email, role_title: data.roleTitle }
    })
    if (!resp) return
    const result = await resp.json()
    if (result.success) {
      setFoundingFounders([...foundingFounders, result.member])
    }
  }

  const handleResendInvitation = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.startupMembers}/${id}/resend_invitation`, "POST", csrfToken)
    if (resp) alert("Invitation resent successfully.")
  }

  const handleRevokeFounder = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.startupMembers}/${id}/revoke`, "DELETE", csrfToken)
    if (!resp) return
    const result = await resp.json()
    if (result.success) {
      setFoundingFounders(foundingFounders.filter((m) => m.id !== id))
    }
  }

  // --- Interview CRUD ---
  const buildInterviewFormData = (interview: Omit<Interview, "id">, method?: string, file?: File | null, removeRec?: boolean) => {
    const fd = new FormData()
    if (method) fd.append("_method", method)
    fd.append("interview[contact_name]", interview.contact)
    fd.append("interview[interview_date]", interview.date)
    fd.append("interview[interview_type]", interviewTypeToRails(interview.type))
    fd.append("interview[key_learnings]", interview.keyInsights)
    fd.append("interview[follow_up_notes]", interview.fullNotes)
    if (file) fd.append("interview[recording]", file)
    if (removeRec) fd.append("interview[remove_recording]", "1")
    return fd
  }

  const handleAddInterview = async (interview: Omit<Interview, "id">, file?: File | null) => {
    if (!apiPaths) {
      setInterviews([...interviews, { ...interview, id: Date.now().toString() }])
      return
    }
    const fd = buildInterviewFormData(interview, undefined, file)
    fd.append("interview[status]", "completed")
    const response = await fetch(apiPaths.interviews, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      body: fd
    })
    if (!response.ok) {
      const data = await response.json().catch(() => null)
      alert(data?.errors?.join(", ") || "Something went wrong")
      return
    }
    const data = await response.json()
    if (data.success) {
      setInterviews([...interviews, data.interview])
    }
  }

  const handleEditInterview = async (id: string, interview: Omit<Interview, "id">, file?: File | null, removeRec?: boolean) => {
    if (!apiPaths) {
      setInterviews(interviews.map((i) => (i.id === id ? { ...interview, id } : i)))
      return
    }
    const fd = buildInterviewFormData(interview, "PATCH", file, removeRec)
    const response = await fetch(`${apiPaths.interviews}/${id}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      body: fd
    })
    if (!response.ok) {
      const data = await response.json().catch(() => null)
      alert(data?.errors?.join(", ") || "Something went wrong")
      return
    }
    const data = await response.json()
    if (data.success) {
      setInterviews(interviews.map((i) => (i.id === id ? data.interview : i)))
    }
  }

  const handleDeleteInterview = async (id: string) => {
    if (!apiPaths) {
      setInterviews(interviews.filter((i) => i.id !== id))
      return
    }
    const resp = await apiCall(`${apiPaths.interviews}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) {
      setInterviews(interviews.filter((i) => i.id !== id))
    }
  }

  return (
    <div className={cn("bg-background", className)}>
          {/* Header */}
          <div className="bg-card border-b border-border">
            <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
              {/* Page Title */}
              <h1 className="text-3xl font-bold text-foreground">My Startup</h1>

              {/* Editable Startup Name */}
              <h2 className="text-xl font-semibold text-foreground">
                {startupName || "Name your startup"}
              </h2>

              {/* Validation Scoreboard */}
              <ValidationScoreboard metrics={{
                interviewsConducted: interviews.length,
                interviewsTarget: 20,
                painPointsValidated: painPoints.filter(p => p.status === "validated").length,
                painPointsTotal: Math.max(painPoints.length, 3),
                painPointStatuses: painPoints.map(p => p.status),
                alternativesValidated: existingAlternatives.filter(a => a.status === "validated").length,
                alternativesTotal: Math.max(existingAlternatives.length, 3),
                alternativeStatuses: existingAlternatives.map(a => a.status),
                contactsAdded: contacts.length,
                validationConfidence: Math.min(
                  interviews.length * 4 +
                  painPoints.filter(p => p.status === "validated").length * 10 +
                  segments.length * 10,
                  100
                ),
                trends: {
                  interviews: "up",
                  painPoints: "up",
                  contacts: "up",
                  confidence: "up"
                }
              }} />

              {/* Team Submissions */}
              <TeamSubmissions
                brainstormingSubmissions={teamBrainstormingSubmissions}
                meetupUploads={teamMeetupUploads}
                interviews={interviews}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="max-w-6xl mx-auto px-6 py-8">
            <Tabs defaultValue="profile" className="space-y-6">
              <TabsList className="flex w-full overflow-x-auto">
                <TabsTrigger value="profile" className="flex-shrink-0 px-4">Profile</TabsTrigger>
                <TabsTrigger value="segments" className="flex-shrink-0 px-4">Customers</TabsTrigger>
                <TabsTrigger value="pain-points" className="flex-shrink-0 px-4">Pain Points</TabsTrigger>
                <TabsTrigger value="alternatives" className="flex-shrink-0 px-4">Alternatives</TabsTrigger>
                <TabsTrigger value="contacts" className="flex-shrink-0 px-4">Contacts</TabsTrigger>
                <TabsTrigger value="interviews" className="flex-shrink-0 px-4">Interviews</TabsTrigger>
                <TabsTrigger value="team" className="flex-shrink-0 px-4">Team</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <StartupProfileTab
                  profile={profile}
                  onSave={handleSaveProfile}
                  isOwner={isOwner}
                />
              </TabsContent>

              <TabsContent value="segments">
                <CustomerSegmentsTab
                  segments={segments}
                  onAdd={handleAddSegment}
                  onEdit={handleEditSegment}
                  onDelete={handleDeleteSegment}
                />
              </TabsContent>

              <TabsContent value="pain-points">
                <PainPointsTab
                  painPoints={painPoints}
                  segments={segments.map(s => ({ id: s.id, name: s.name }))}
                  onAdd={handleAddPainPoint}
                  onEdit={handleEditPainPoint}
                  onDelete={handleDeletePainPoint}
                />
              </TabsContent>

              <TabsContent value="alternatives">
                <ExistingAlternativesTab
                  alternatives={existingAlternatives}
                  segments={segments.map(s => ({ id: s.id, name: s.name }))}
                  onAdd={handleAddAlternative}
                  onEdit={handleEditAlternative}
                  onDelete={handleDeleteAlternative}
                />
              </TabsContent>

              <TabsContent value="contacts">
                <ContactsTab
                  contacts={contacts}
                  segments={segments.map(s => ({ id: s.id, name: s.name }))}
                  onAdd={handleAddContact}
                  onEdit={handleEditContact}
                  onDelete={handleDeleteContact}
                  onLogInterview={(contactId) => {
                    window.location.href = `/founder/interviews/new?contact_id=${contactId}`
                  }}
                  bulkImportPath={apiPaths?.bulkImportContacts}
                  importTemplatePath={apiPaths?.importContactsTemplate}
                  csrfToken={csrfToken}
                />
              </TabsContent>

              <TabsContent value="interviews">
                <InterviewsTab
                  interviews={interviews}
                  segments={segments.map(s => ({ id: s.id, name: s.name }))}
                  onAdd={handleAddInterview}
                  onEdit={handleEditInterview}
                  onDelete={handleDeleteInterview}
                />
              </TabsContent>

              <TabsContent value="team">
                <FoundingFoundersTab
                  members={foundingFounders}
                  isOwner={isOwner}
                  onInvite={handleInviteFounder}
                  onResend={handleResendInvitation}
                  onRevoke={handleRevokeFounder}
                />
              </TabsContent>
            </Tabs>
          </div>
    </div>
  )
}
